import { ArrowRightIcon, CalendarBlankIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { readLocationIds } from 'app/components/action-center/locationParam'
import { usePmsIntegration } from 'app/hooks/usePmsIntegration'

/**
 * The way through to the week's chairs.
 *
 * Sits above the queues because it is the other half of all of them: those
 * cards say who is waiting, and the calendar says what time there is to put
 * them in.
 *
 * Deliberately a plain link with no count on it. Reading the week costs four
 * requests to the availability engine per location, and this card sits below
 * the fold, so paying for it on every Action Center load meant most of those
 * calls were spent on a number nobody scrolled to. The calendar asks the engine
 * when somebody opens it, which is when the answer is worth having.
 */
export function WeekOpeningsCard() {
  const [searchParams] = useSearchParams()
  const { scheduleGate } = usePmsIntegration()
  // A filtered Action Center opens the calendar on the same location.
  const filtered = useMemo(() => readLocationIds(searchParams.get(`filters`)), [searchParams])
  const href =
    filtered.length === 1
      ? `/action-center/schedule?locationId=${encodeURIComponent(filtered[0])}`
      : `/action-center/schedule`

  if (scheduleGate === `hidden`) return null

  return (
    <section className="mt-8">
      <div className="flex items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-ui-sm text-strong">
            <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
            The week ahead
          </p>
          <p className="mt-0.5 text-body-xs text-subtle">Where the people above actually go</p>
        </div>
      </div>
      <div className="mt-3 h-px bg-gradient-to-r from-emerald-200 to-transparent" />

      <Link
        to={href}
        className="mt-5 flex items-center gap-5 rounded-xl border border-default bg-surface bg-linear-to-b from-emerald-500/[0.07] to-transparent to-60% p-6 shadow-elevation-2 transition hover:shadow-elevation-4"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
          <CalendarBlankIcon size={22} weight="bold" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-ui-md text-strong">See where the week is open</span>
          <span className="mt-1 block text-body-sm leading-relaxed text-subtle">
            Your chairs for the week, with every opening the practice can still sell. Click one to see who is waiting
            for it.
          </span>
        </span>

        <span className="hidden shrink-0 items-center gap-1.5 rounded-lg bg-inverse px-3 py-2 text-body-sm text-inverse sm:inline-flex">
          Open the week <ArrowRightIcon size={13} weight="bold" />
        </span>
      </Link>
    </section>
  )
}
